import Bugsnag from '@bugsnag/js';
import type { TFunction } from 'i18next';

import type { TenantTheme } from '../../backend/src/utils';
import { WATTS_PER_KILOWATT } from '../constants/ui';
import type { Config } from '../models/config';
import { CONFIG_KEY, LocalStorage } from '../storage';

export * from './date-time';

const DARK_THEME: TenantTheme = 'dark';
const DEFAULT_AMOUNT_DECIMAL_PLACES = 2;

/** BCP 47 locale for amount formatting, set per tenant via settings.amount_locale; undefined keeps the historical toFixed output. */
let activeAmountLocale: string | undefined;

export const setAmountLocale = (amountLocale: string | undefined) => {
  activeAmountLocale = amountLocale;
};

export const capitalizeFirstLetter = (value: string | undefined): string => {
  if (!value) {
    return '';
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const decapitalizeFirstLetter = (value: string | undefined): string => {
  if (!value) {
    return '';
  }

  return value.charAt(0).toLowerCase() + value.slice(1);
};

export const convertWattToKilowatt = (watt: number): number => {
  // Handle null, undefined, NaN, or invalid inputs
  if (watt === null || watt === undefined || isNaN(watt) || typeof watt !== 'number') {
    return 0;
  }

  // Handle edge cases like Infinity
  if (!isFinite(watt)) {
    return 0;
  }

  return Number((watt / WATTS_PER_KILOWATT).toFixed(2));
};

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
export const isValidEmail = (email: string) => {
  return EMAIL_REGEX.test(email.trim());
};

export const checkPinCode = (
  pin: string,
  translate: TFunction<'translation', undefined>
): { isInvalid: boolean; errorMessage: string | null } => {
  // Check if the pin is a string of exactly 4 digits
  if (!/^\d{4}$/.test(pin)) {
    return { isInvalid: true, errorMessage: translate('app.create_pin_code.error.message.four.digit.requirement') };
  }

  // Check for 4 consecutive incremental digits
  if (/0123|1234|2345|3456|4567|5678|6789/.test(pin)) {
    return {
      isInvalid: true,
      errorMessage: translate('app.create_pin_code.error.message.incremental.digits.requirement'),
    };
  }

  // Check for the same 4 digits
  if (/^(\d)\1{3}$/.test(pin)) {
    return { isInvalid: true, errorMessage: translate('app.create_pin_code.error.message.same.digits.requirement') };
  }

  // If none of the above conditions are met, the pin is valid
  return { isInvalid: false, errorMessage: null };
};

// debit/credit card expiration date => month validation
export const isValidMonth = (month: string | undefined): boolean => {
  if (!month) {
    return false;
  }

  const validMonths = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  return validMonths.includes(month);
};

export const checkLast4 = (pin: string, translate: TFunction<'translation', undefined>) => {
  if (/^(\d)\1{3}$/.test(pin)) {
    return { isInvalid: true, errorMessage: translate('app.create_pin_code.error.message.same.digits.requirement') };
  }

  if (!/^\d{4}$/.test(pin)) {
    return { isInvalid: true, errorMessage: translate('app.create_pin_code.error.message.four.digit.requirement') };
  }

  return { isInvalid: false, errorMessage: null };
};

// debit/credit card expiration date => year validation
export const isValidYear = (year: string | undefined): boolean => {
  if (!year) {
    return false;
  }

  if (year.length > 4 || year.length < 2) {
    return false;
  }

  if (year.length === 3) {
    return false;
  }

  const currentYear = new Date().getFullYear();
  const inputYear = parseInt(year.length > 2 ? year : `20${year}`);
  return !isNaN(inputYear) && inputYear >= currentYear;
};

export const setCssColorVariables = (primary: string | undefined, secondary: string | undefined) => {
  const root: HTMLElement | null = document.querySelector(':root');

  if (primary) {
    root?.style.setProperty('--primary', primary);
    root?.style.setProperty('--primary-gradient', primary);
  }

  if (secondary) {
    root?.style.setProperty('--secondary', secondary);
  }
};

export const setThemeAttribute = (theme: TenantTheme | undefined) => {
  if (theme === DARK_THEME) {
    document.documentElement.dataset.theme = DARK_THEME;
  } else {
    delete document.documentElement.dataset.theme;
  }
};

export const setConfigurationData = (config: Config | undefined) => {
  if (!config) {
    return;
  }
  LocalStorage.setItem(CONFIG_KEY, config);
  setCssColorVariables(config.colorPrimary, config.colorSecondary);
  setThemeAttribute(config.theme);
  setAmountLocale(config.amountLocale);
  document.title = config.displayName ?? '';
};

export const handleAndMonitorError = (error: string | Error, metadata?: Record<string, unknown>) => {
  if (process.env.REACT_APP_ENV === 'local') return;

  const err = typeof error === 'string' ? new Error(error) : error;

  // Preserve original error message and stack trace
  if (typeof error === 'string') {
    err.name = 'ApplicationError';
    err.message = error;
  }

  Bugsnag.notify(err, event => {
    // Ensure error message is preserved and not minified
    if (event.errors[0]) {
      event.errors[0].errorMessage = err.message;
      event.errors[0].errorClass = err.name || 'Error';

      // Add original error information if it exists
      if (err.stack) {
        event.addMetadata('originalError', {
          message: err.message,
          name: err.name,
          stack: err.stack,
        });
      }
    }

    // Add enhanced context metadata
    event.addMetadata('errorContext', {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorType: metadata?.errorType || 'manual',
    });

    if (metadata) {
      Object.entries(metadata).forEach(([section, data]) => {
        event.addMetadata(section, data as Record<string, unknown>);
      });
    }

    // Add script loading context for cross-origin errors
    if (metadata?.errorType === 'crossOriginError') {
      // Log all currently loaded scripts
      const scripts = Array.from(document.getElementsByTagName('script')).map(script => ({
        src: script.src || 'inline',
        crossOrigin: script.crossOrigin,
        async: script.async,
        defer: script.defer,
      }));
      event.addMetadata('loadedScripts', { scripts });
    }

    // Add React component stack if available
    try {
      const reactFiberNode = (err as any).__reactInternalFiber || (err as any)._reactInternalFiber;
      if (reactFiberNode) {
        event.addMetadata('react', {
          componentStack: 'React component error detected',
          fiberNodeType: reactFiberNode.type?.name || 'Unknown',
        });
      }
    } catch (e) {
      // Ignore if we can't get React info
    }

    return true;
  });
};

/** Cached per locale/decimals/currency; `null` records a combination Intl rejected so it is not retried every render. */
const amountFormatters = new Map<string, Intl.NumberFormat | null>();

const getAmountFormatter = (locale: string, decimalPlaces: number, currencyCode?: string): Intl.NumberFormat | null => {
  const key = `${locale}|${decimalPlaces}|${currencyCode ?? ''}`;
  if (amountFormatters.has(key)) {
    return amountFormatters.get(key) ?? null;
  }
  let formatter: Intl.NumberFormat | null;
  try {
    formatter = new Intl.NumberFormat(locale, {
      ...(currencyCode ? { style: 'currency', currency: currencyCode } : {}),
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  } catch {
    // Malformed locale tag or unknown ISO currency: callers fall back to the historical format.
    formatter = null;
  }
  amountFormatters.set(key, formatter);
  return formatter;
};

export const displayAmount = (value: number, currencyDecimalPlaces = DEFAULT_AMOUNT_DECIMAL_PLACES): string => {
  const formatter = activeAmountLocale ? getAmountFormatter(activeAmountLocale, currencyDecimalPlaces) : null;
  if (formatter) {
    return formatter.format(value || 0);
  }
  return value ? value.toFixed(currencyDecimalPlaces) : '0.00';
};

export const displayData = (
  value: number,
  currencyPrefix?: string,
  currencySuffix?: string,
  currencyDecimalPlaces?: number,
  currency?: string
): string | null => {
  if (!value) {
    return null;
  }
  if (currencyPrefix || currencySuffix) {
    return `${currencyPrefix || ''}${displayAmount(value, currencyDecimalPlaces)}${currencySuffix || ''}`.trim();
  } else {
    return `${displayAmount(value, currencyDecimalPlaces)} ${currency}`.trim();
  }
};

/**
 * Amount with its currency symbol (forecast cells, axis ticks). Zero renders as an amount, not null.
 * Without a tenant amount locale the symbol is prefixed as before; with one, Intl places the symbol
 * per locale (e.g. "12,50 Kč"), falling back to the prefixed symbol if Intl rejects the currency code.
 */
export const formatSymbolAmount = (
  currencyCode: string | undefined,
  currencySymbol: string | undefined,
  value: number | string | undefined,
  decimalPlaces = DEFAULT_AMOUNT_DECIMAL_PLACES
): string => {
  const amount = Number(value) || 0;
  const formatter =
    activeAmountLocale && currencyCode ? getAmountFormatter(activeAmountLocale, decimalPlaces, currencyCode) : null;
  if (formatter) {
    return formatter.format(amount);
  }
  return `${currencySymbol ?? ''}${displayAmount(amount, decimalPlaces)}`;
};

export const calculateMultiPrice = (
  multiPriceData?: { feePerKwh?: number; feePerUnit?: number; flatFee: number }[]
): number | undefined => {
  if (!multiPriceData || multiPriceData.length === 0) return undefined;

  const fee = multiPriceData[0].feePerKwh ?? multiPriceData[0].feePerUnit ?? 0;
  const total = fee + multiPriceData[0].flatFee;

  return Number(total.toFixed(2));
};

// Safe JSON parsing utility for error responses
export const safeParseErrorResponse = (
  errorData: any
): {
  message: string;
  localeTitleKey?: string;
  localeDescrKey?: string;
} => {
  try {
    if (typeof errorData === 'string') {
      const parsed = JSON.parse(errorData);
      return {
        message: parsed.message || 'Unknown error',
        localeTitleKey: parsed.localeTitleKey,
        localeDescrKey: parsed.localeDescrKey,
      };
    } else if (typeof errorData === 'object' && errorData !== null) {
      return {
        message: errorData.message || 'Unknown error',
        localeTitleKey: errorData.localeTitleKey,
        localeDescrKey: errorData.localeDescrKey,
      };
    }
  } catch (parseError) {
    console.warn('Failed to parse error response:', parseError);
  }

  // Fallback for unparseable errors
  return {
    message: String(errorData) || 'An unexpected error occurred',
    localeTitleKey: 'app.errors.title.general',
    localeDescrKey: 'app.errors.description.general',
  };
};
